import { useEffect, useRef, useState, useCallback } from "react";
import { useChat } from "../../hooks/useChat";
import { useChatsStore } from "../../store/chats";
import { unlockAudio, enqueue, cancelAll, isPlaying, setConversationActive } from "../../lib/globalTts";
import { Mic, X, AudioLines } from "lucide-react";

type State = "idle" | "listening" | "thinking" | "speaking";

function sanitize(t: string): string {
  return t
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/^#+\s+/gm, "")
    .replace(/\n{2,}/g, ". ")
    .replace(/\n/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function splitForQueue(text: string): string[] {
  if (text.length <= 200) return [text];
  const parts: string[] = [];
  let rest = text;
  while (rest.length > 200) {
    let cut = rest.lastIndexOf(". ", 200);
    if (cut < 50) cut = rest.lastIndexOf(", ", 200);
    if (cut < 50) cut = rest.lastIndexOf(" ", 200);
    if (cut < 50) cut = 200;
    parts.push(rest.slice(0, cut + 1).trim());
    rest = rest.slice(cut + 1).trim();
  }
  if (rest) parts.push(rest);
  return parts.filter(p => p.length > 3);
}

const STT_PATH = "/v1/audio/transcriptions";
const SILENCE_TIMEOUT_MS = 1800;
const MAX_RECORD_MS = 30000;

let _analyserCtx: AudioContext | null = null;
function getAnalyserCtx(): AudioContext {
  if (!_analyserCtx) {
    const AC = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
    _analyserCtx = new AC();
  }
  return _analyserCtx;
}

export function ConversationOverlay({ onClose, onState }: { onClose: () => void; onState?: (s: State) => void }) {
  const { send, streaming } = useChat();
  const activeSession = useChatsStore((s) => s.sessions.find((ss) => ss.id === s.activeId));
  const [state, setState] = useState<State>("idle");
  const [, setPartial] = useState("");
  const [error, setError] = useState<string | null>(null);
  const activeRef = useRef(true);
  const abortRef = useRef(new AbortController());
  const baseRef = useRef(0);
  const spokenRef = useRef(0);
  const busyRef = useRef(false);
  const qRef = useRef<string[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const streamingRef = useRef(false);
  useEffect(() => { streamingRef.current = streaming; }, [streaming]);

  const supported = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

  useEffect(() => { onState?.(state); }, [state, onState]);

  const drain = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    const ctrl = abortRef.current;
    while (!ctrl.signal.aborted) {
      while (qRef.current.length > 0 && !ctrl.signal.aborted) {
        enqueue(qRef.current.shift()!, { role: "good" });
      }
      while (isPlaying() && !ctrl.signal.aborted) {
        await new Promise((r) => setTimeout(r, 150));
      }
      if (qRef.current.length === 0) break;
    }
    if (ctrl.signal.aborted) cancelAll();
    busyRef.current = false;
    if (qRef.current.length > 0 && !ctrl.signal.aborted && activeRef.current) {
      drain();
      return;
    }
    if (!ctrl.signal.aborted && activeRef.current && !streamingRef.current) {
      setState("idle");
      startListen();
    }
  }, []);

  const startPoll = useCallback(() => {}, []);

  useEffect(() => {
    if (streaming || (state !== "thinking" && state !== "speaking")) return;
    const t = setTimeout(() => {
      if (streamingRef.current) return;
      const st = useChatsStore.getState();
      const sess = st.sessions.find(s => s.id === st.activeId);
      if (!sess) return;
      const msgs = sess.messages.slice(baseRef.current);
      const last = [...msgs].reverse().find(m => m.role === "assistant" && (m.text || "").trim().length > 0);
      if (!last) return;
      const rem = sanitize(last.text || "").slice(spokenRef.current).trim();
      if (rem.length > 5) { spokenRef.current += rem.length; qRef.current.push(...splitForQueue(rem)); setState("speaking"); drain(); }
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }, 1200);
    return () => clearTimeout(t);
  }, [streaming, state, drain]);

  async function startListen() {
    if (!activeRef.current) return;
    setError(null);
    setPartial("");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setState("listening");

      const mime = getSupportedMime();
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recRef.current = recorder;
      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        if (!activeRef.current) return;
        if (chunks.length === 0) { setState("idle"); return; }

        const blob = new Blob(chunks, { type: recorder.mimeType });
        if (blob.size < 1000) { setState("idle"); startListen(); return; }

        setState("thinking");
        setPartial("");

        try {
          const form = new FormData();
          form.append("file", blob, mimeToFilename(recorder.mimeType));
          const r = await fetch(STT_PATH, { method: "POST", body: form, credentials: "include" });
          if (!r.ok) throw new Error(`STT HTTP ${r.status}`);
          const { text } = await r.json();
          setPartial("");

          if (text && text.trim()) {
            abortRef.current.abort(); abortRef.current = new AbortController(); cancelAll();
            spokenRef.current = 0; qRef.current = []; busyRef.current = false;
            baseRef.current = activeSession?.messages.length ?? 0;
            unlockAudio(); startPoll();
            send(text.trim(), { voice: true }).catch(e => setError((e as Error).message));
          } else {
            if (activeRef.current) startListen();
          }
        } catch (e: any) {
          setError(e.message || "STT error");
          setState("idle");
        }
      };

      recorder.start();

      const ctx = getAnalyserCtx();
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      let silenceStart = 0;
      const startTime = Date.now();

      const check = setInterval(() => {
        if (recorder.state !== "recording") { clearInterval(check); try { src.disconnect(); } catch {} return; }
        if (Date.now() - startTime > MAX_RECORD_MS) { clearInterval(check); try { src.disconnect(); } catch {} recorder.stop(); return; }
        analyser.getByteFrequencyData(buf);
        const avg = buf.reduce((a, b) => a + b, 0) / buf.length;
        if (avg < 8) {
          if (!silenceStart) silenceStart = Date.now();
          else if (Date.now() - silenceStart > SILENCE_TIMEOUT_MS) {
            clearInterval(check); try { src.disconnect(); } catch {} recorder.stop();
          }
        } else {
          silenceStart = 0;
        }
      }, 100);

    } catch (e: any) {
      setError(e.message || "Microphone error");
      setState("idle");
    }
  }

  function stop() {
    abortRef.current.abort(); abortRef.current = new AbortController();
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    try { recRef.current?.stop(); } catch {}
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
    qRef.current = []; busyRef.current = false;
  }

  useEffect(() => {
    if (!supported) { setError("Микрофон недоступен."); return; }
    setConversationActive(true);
    unlockAudio();
    startListen();
    return () => { activeRef.current = false; stop(); setConversationActive(false); };
  }, []);

  const ring = state === "listening" ? "ring-indigo-400/70 bg-indigo-500/20" : state === "thinking" ? "ring-amber-400/70 bg-amber-500/20" : state === "speaking" ? "ring-emerald-400/70 bg-emerald-500/20" : "ring-slate-400/40 bg-slate-500/10";
  const onIconClick = () => {
    if (state === "speaking") { stop(); if (activeRef.current) startListen(); return; }
    if (state === "listening") { try { recRef.current?.stop(); } catch { /* ignore */ } return; }
    startListen();
  };
  return error ? (
    <div className="fixed bottom-20 left-1/2 z-50 -translate-x-1/2">
      <span className="rounded-full bg-rose-500/90 px-3 py-1 text-xs text-white shadow">{error}</span>
    </div>
  ) : null;
}

function getSupportedMime(): string {
  if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) return "audio/webm;codecs=opus";
  if (MediaRecorder.isTypeSupported("audio/webm")) return "audio/webm";
  if (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")) return "audio/ogg;codecs=opus";
  if (MediaRecorder.isTypeSupported("audio/mp4")) return "audio/mp4";
  if (MediaRecorder.isTypeSupported("audio/aac")) return "audio/aac";
  return "";
}

function mimeToFilename(mime: string): string {
  if (mime.includes("mp4") || mime.includes("aac")) return "audio.mp4";
  if (mime.includes("ogg")) return "audio.ogg";
  return "audio.webm";
}
